@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=Inter:wght@400;500;600;700&display=swap');

:root {
  /* Colors - Modern Clean Aviation SaaS */
  --color-primary: #2563eb;
  --color-primary-hover: #1d4ed8;
  --color-primary-light: #eff6ff;
  --color-primary-subtle: #dbeafe;

  --color-bg-page: #f3f5f9;
  --color-bg-card: #ffffff;
  --color-bg-subtle: #f8fafc;

  --color-text-main: #0f172a;
  --color-text-secondary: #334155;
  --color-text-muted: #64748b;
  --color-text-subtle: #94a3b8;
  --color-border: #e9eef5;
  --color-border-subtle: #f1f4f9;

  --color-success: #16a34a;
  --color-danger: #dc2626;
  --color-warning: #d97706;

  /* Layout */
  --sidebar-width: 264px;
  --sidebar-width-collapsed: 72px;
  --header-height: 68px;

  /* Shadow & Radius */
  --shadow-xs: 0 1px 2px 0 rgba(0, 0, 0, 0.04);
  --shadow-sm: 0 1px 3px 0 rgba(0, 0, 0, 0.05), 0 1px 2px -1px rgba(0, 0, 0, 0.04);
  --shadow-md: 0 4px 12px -2px rgba(15, 23, 42, 0.06), 0 2px 6px -1px rgba(15, 23, 42, 0.03);
  --shadow-lg: 0 10px 25px -5px rgba(15, 23, 42, 0.08), 0 8px 10px -6px rgba(15, 23, 42, 0.04);
  --shadow-card: 0 1px 3px rgba(0, 0, 0, 0.02), 0 4px 12px -2px rgba(15, 23, 42, 0.03);

  --radius-xs: 6px;
  --radius-sm: 8px;
  --radius-md: 12px;
  --radius-lg: 16px;
  --radius-xl: 20px;
  --radius-full: 9999px;
}

* {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

html,
body,
#root {
  margin: 0;
  padding: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  box-sizing: border-box;
}

*, *::before, *::after {
  box-sizing: border-box;
}

body {
  font-family: 'Plus Jakarta Sans', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  background-color: var(--color-bg-page);
  color: var(--color-text-main);
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

/* App Main Container Layout */
.app-container {
  display: flex;
  height: 100vh;
  width: 100%;
  max-width: 100vw;
  overflow: hidden;
  background-color: var(--color-bg-page);
}

/* Main Content Area (Wraps top-header and page-container) */
.main-content {
  flex: 1 1 auto;
  min-width: 0;
  width: 100%;
  height: 100vh;
  max-height: 100vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
  background-color: var(--color-bg-page);
}

/* ========================================================================= */
/* LEFT SIDEBAR                                                              */
/* ========================================================================= */
.sidebar-wrapper {
  width: var(--sidebar-width);
  height: 100vh;
  flex-shrink: 0;
  position: relative;
  transition: width 0.24s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 40;
}

.sidebar-wrapper.collapsed {
  width: var(--sidebar-width-collapsed);
}

.sidebar {
  width: var(--sidebar-width);
  background-color: var(--color-bg-card);
  border-right: 1px solid var(--color-border);
  display: flex;
  flex-direction: column;
  padding: 18px 12px 16px 12px;
  height: 100vh;
  overflow: visible;
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  transition: width 0.24s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.24s cubic-bezier(0.4, 0, 0.2, 1), padding 0.24s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 40;
}

.sidebar.collapsed {
  width: var(--sidebar-width-collapsed);
  padding: 18px 8px 16px 8px;
}

.sidebar.collapsed.hover-expanded {
  width: var(--sidebar-width);
  padding: 18px 12px 16px 12px;
  z-index: 60;
  box-shadow: 6px 0 28px rgba(15, 23, 42, 0.14), 0 0 0 1px rgba(226, 232, 240, 0.9);
}

/* Modern Floating Edge Toggle Button */
.sidebar-edge-toggle {
  position: absolute;
  right: -13px;
  top: 22px;
  width: 26px;
  height: 26px;
  background: #ffffff;
  border: 1px solid #cbd5e1;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #64748b;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.12);
  z-index: 100;
  transition: all 0.18s cubic-bezier(0.4, 0, 0.2, 1);
  padding: 0;
}

.sidebar-edge-toggle:hover {
  background: #f8fafc;
  color: #2563eb;
  border-color: #93c5fd;
  box-shadow: 0 2px 8px rgba(37, 99, 235, 0.25);
  transform: scale(1.12);
}

/* Brand Header */
.sidebar-brand-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 22px;
  padding: 0 4px;
  position: relative;
}

.sidebar-brand-main {
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  text-decoration: none;
  min-width: 0;
  flex: 1;
}

.sidebar-logo-img {
  width: 34px;
  height: 34px;
  object-fit: contain;
  border-radius: 8px;
  flex-shrink: 0;
}

.sidebar-brand-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
  overflow: hidden;
}

.sidebar-brand-title {
  font-size: 19px;
  font-weight: 800;
  letter-spacing: 0.12em;
  color: #0f172a;
  line-height: 1.1;
}

.sidebar-brand-slogan {
  font-size: 8px;
  font-weight: 700;
  letter-spacing: 0.16em;
  color: #64748b;
  text-transform: uppercase;
  margin-top: 3px;
  white-space: nowrap;
}

.sidebar.collapsed .sidebar-brand-header {
  justify-content: center;
  padding: 0;
}

.sidebar.collapsed .sidebar-brand-main {
  justify-content: center;
  flex: none;
}

.sidebar.collapsed.hover-expanded .sidebar-brand-header {
  justify-content: space-between;
  padding: 0 4px;
}

.sidebar.collapsed.hover-expanded .sidebar-brand-main {
  justify-content: flex-start;
  flex: 1;
}

.sidebar.collapsed.hover-expanded .nav-item {
  justify-content: flex-start;
  padding: 9px 12px;
}

.sidebar.collapsed.hover-expanded .sidebar-accordion-header {
  justify-content: space-between;
  padding: 9px 10px;
}

/* Sidebar Nav */
.sidebar-nav {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex-grow: 1;
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 #f8fafc;
}
.sidebar-nav::-webkit-scrollbar {
  width: 5px;
}
.sidebar-nav::-webkit-scrollbar-track {
  background: transparent;
}
.sidebar-nav::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 4px;
}

.nav-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 12px;
  border-radius: var(--radius-sm);
  color: #475569;
  text-decoration: none;
  font-weight: 500;
  font-size: 13px;
  transition: all 0.15s ease;
  cursor: pointer;
  border: none;
  background: transparent;
  width: 100%;
  text-align: left;
}

.nav-item:hover {
  background-color: #f8fafc;
  color: #0f172a;
}

.nav-item.active {
  background-color: #f0f4fa;
  color: #0f172a;
  font-weight: 600;
}

.nav-item-dashboard.active {
  background-color: #edf2f9;
  color: #0f172a;
  font-weight: 700;
  border-radius: 10px;
}

.nav-item svg {
  width: 18px;
  height: 18px;
  flex-shrink: 0;
}

.sidebar.collapsed .nav-item {
  justify-content: center;
  padding: 10px 0;
}

/* Sidebar Accordion Groups */
.sidebar-accordion-group {
  border-radius: 10px;
  margin-top: 2px;
}

.sidebar-accordion-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 9px 10px;
  border: none;
  background: transparent;
  border-radius: 8px;
  cursor: pointer;
  color: #1e293b;
  font-weight: 600;
  font-size: 13px;
  text-align: left;
  transition: all 0.15s ease;
}

.sidebar-accordion-header:hover {
  background-color: #f8fafc;
  color: #0f172a;
}

.sidebar-accordion-header.has-active {
  color: #2563eb;
  background-color: #f8fafc;
}

.sidebar-accordion-title-wrap {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.sidebar-accordion-title-text {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 13px;
  font-weight: 600;
}

.sidebar-accordion-chevron {
  color: #94a3b8;
  display: flex;
  align-items: center;
  flex-shrink: 0;
  transition: transform 0.2s ease;
}

.sidebar.collapsed .sidebar-accordion-header {
  justify-content: center;
  padding: 10px 0;
}

.sidebar-accordion-items {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 3px 0 5px 12px;
  margin-left: 10px;
  border-left: 1.5px solid #e9eef5;
}

.sidebar-accordion-items .nav-item {
  padding: 7px 10px;
  font-size: 12.5px;
  color: #64748b;
}

.sidebar-accordion-items .nav-item:hover {
  color: #0f172a;
}

.sidebar-accordion-items .nav-item.active {
  color: #2563eb;
  background-color: #eff6ff;
}

.sidebar-divider {
  height: 1px;
  background-color: var(--color-border);
  margin: 10px 4px;
}

/* Sidebar Footer */
.sidebar-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 6px 0 6px;
  border-top: 1px solid var(--color-border);
  margin-top: auto;
}

.sidebar-footer-user {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  cursor: pointer;
  flex: 1;
}

.sidebar-footer-meta {
  flex: 1;
  min-width: 0;
}

.sidebar-footer-name {
  font-size: 13px;
  font-weight: 600;
  color: #0f172a;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sidebar-footer-role {
  font-size: 11px;
  color: #64748b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sidebar-logout-icon-btn {
  background: transparent;
  border: none;
  cursor: pointer;
  color: #94a3b8;
  padding: 6px;
  border-radius: 6px;
  display: flex;
  align-items: center;
}

.sidebar-logout-icon-btn:hover {
  background-color: #fee2e2;
  color: #dc2626;
}

.sidebar.collapsed .sidebar-footer {
  justify-content: center;
  padding: 12px 0 0 0;
}

/* ========================================================================= */
/* TOP HEADER                                                                */
/* ========================================================================= */
.top-header {
  height: var(--header-height);
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 24px;
  background-color: var(--color-bg-card);
  border-bottom: 1px solid var(--color-border);
  flex-shrink: 0;
  width: 100%;
  box-sizing: border-box;
  min-width: 0;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  flex: 1 1 auto;
}

/* Search Bar */
.search-bar {
  background-color: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: var(--radius-full);
  padding: 7px 14px;
  display: flex;
  align-items: center;
  gap: 8px;
  width: 280px;
  max-width: 100%;
  transition: all 0.15s ease;
  min-width: 0;
  flex-shrink: 1;
}

.search-bar:focus-within {
  background-color: #ffffff;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.search-icon {
  color: #94a3b8;
  flex-shrink: 0;
}

.search-bar input {
  border: none;
  background: transparent;
  outline: none;
  width: 100%;
  color: var(--color-text-main);
  font-size: 13px;
  font-family: inherit;
}

.search-bar input::placeholder {
  color: #94a3b8;
}

.search-kbd-shortcut {
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
  background: #ffffff;
  border: 1px solid #cbd5e1;
  border-radius: 5px;
  padding: 1px 6px;
  line-height: 1.4;
  flex-shrink: 0;
}

/* Dark Submit Report Button */
.btn-submit-report {
  background-color: #0b1329;
  color: #ffffff;
  border: none;
  border-radius: var(--radius-full);
  padding: 8px 18px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
  font-family: inherit;
}

.btn-submit-report:hover {
  background-color: #1e293b;
  transform: translateY(-1px);
  box-shadow: 0 4px 10px rgba(11, 19, 41, 0.2);
}

.btn-submit-report:active {
  transform: translateY(0);
}

/* Header Right Area */
.header-right {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
}

/* Date Button */
.header-date-pill-btn {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: var(--radius-full);
  padding: 7px 16px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 500;
  color: #1e293b;
  cursor: pointer;
  transition: all 0.15s ease;
  font-family: inherit;
}

.header-date-pill-btn:hover {
  background-color: #f8fafc;
  border-color: #cbd5e1;
  color: #0f172a;
}

.header-date-pill-btn svg {
  color: #64748b;
}

/* Notification Button */
.header-notif-btn {
  position: relative;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 50%;
  width: 38px;
  height: 38px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #475569;
  cursor: pointer;
  transition: all 0.15s ease;
}

.header-notif-btn:hover {
  background-color: #f8fafc;
  color: #0f172a;
  border-color: #cbd5e1;
}

.header-notif-badge {
  position: absolute;
  top: -2px;
  right: -2px;
  background-color: #ef4444;
  color: #ffffff;
  font-size: 10px;
  font-weight: 700;
  border-radius: var(--radius-full);
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid #ffffff;
}

.avatar-circle {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background-color: var(--color-primary);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 13px;
}

/* Content Page Container */
.page-container {
  flex: 1 1 0%;
  min-width: 0;
  width: 100%;
  height: calc(100vh - var(--header-height));
  max-height: calc(100vh - var(--header-height));
  overflow-y: auto;
  overflow-x: hidden;
  padding: 24px 28px 48px 28px;
  background-color: var(--color-bg-page);
  box-sizing: border-box;
}

.page-container::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}
.page-container::-webkit-scrollbar-track {
  background: #f1f5f9;
}
.page-container::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 4px;
}
.page-container::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

/* Hero / Section Header Banner */
.page-header-banner {
  background: linear-gradient(135deg, #eff6ff 0%, #e0e7ff 100%);
  border: 1px solid #dbeafe;
  border-radius: var(--radius-lg);
  padding: 24px 28px;
  margin-bottom: 24px;
}

.page-header-banner .badge {
  color: #3b82f6;
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 6px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.page-header-banner h1 {
  font-size: 26px;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 8px;
}

.page-header-banner p {
  color: #475569;
  font-size: 14px;
  line-height: 1.5;
  margin: 0;
}

/* Module Grid */
.module-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 20px;
}

.module-card {
  background-color: var(--color-bg-card);
  border-radius: var(--radius-lg);
  padding: 22px;
  box-shadow: var(--shadow-sm);
  border: 1px solid var(--color-border);
  display: flex;
  gap: 16px;
  align-items: flex-start;
  transition: transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease;
  cursor: pointer;
}

.module-card:hover {
  transform: translateY(-2px);
  box-shadow: var(--shadow-md);
  border-color: #bfdbfe;
}

.module-icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background-color: var(--color-primary-light);
  color: var(--color-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.module-content h3 {
  font-size: 15px;
  font-weight: 600;
  color: #0f172a;
  margin-bottom: 4px;
}

.module-content p {
  font-size: 13px;
  color: var(--color-text-muted);
  line-height: 1.45;
  margin: 0;
}

/* Buttons */
.btn-primary {
  background-color: var(--color-primary);
  color: white;
  border: none;
  border-radius: var(--radius-full);
  padding: 10px 20px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.15s ease;
  width: 100%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.btn-primary:hover {
  background-color: var(--color-primary-hover);
}

.btn-secondary {
  background-color: #ffffff;
  border: 1px solid var(--color-border);
  color: var(--color-text-main);
  border-radius: var(--radius-full);
  padding: 8px 18px;
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.15s ease;
}

.btn-secondary:hover {
  background-color: var(--color-bg-page);
  border-color: #cbd5e1;
}

/* Card */
.card {
  background-color: var(--color-bg-card);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-sm);
  padding: 28px;
  margin-bottom: 24px;
  border: 1px solid var(--color-border);
}

/* Utility Helpers */
.flex { display: flex; }
.flex-col { flex-direction: column; }
.items-center { align-items: center; }
.justify-between { justify-content: space-between; }
.gap-2 { gap: 8px; }
.gap-4 { gap: 16px; }

/* Tabs Navigation */
.nav-tabs {
  display: flex;
  gap: 8px;
  border-bottom: 1px solid var(--color-border);
  margin-bottom: 24px;
  overflow-x: auto;
}

.tab-btn {
  padding: 10px 18px;
  font-size: 14px;
  font-weight: 500;
  color: var(--color-text-muted);
  background: transparent;
  border: none;
  border-bottom: 2px solid transparent;
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.tab-btn:hover {
  color: var(--color-text-main);
}

.tab-btn.active {
  color: var(--color-primary);
  border-bottom-color: var(--color-primary);
  font-weight: 600;
}

/* Stat & KPI Cards */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
}

.stat-card {
  background-color: var(--color-bg-card);
  border-radius: var(--radius-md);
  border: 1px solid var(--color-border);
  box-shadow: var(--shadow-xs);
  padding: 18px 20px;
  display: flex;
  flex-direction: column;
  transition: box-shadow 0.15s ease;
}

.stat-card:hover {
  box-shadow: var(--shadow-sm);
}

.stat-card .stat-label {
  font-size: 12px;
  font-weight: 600;
  color: var(--color-text-muted);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.stat-card .stat-value {
  font-size: 26px;
  font-weight: 700;
  color: var(--color-text-main);
  margin-top: 6px;
}

.stat-card .stat-sub {
  font-size: 12px;
  color: var(--color-text-muted);
  margin-top: 4px;
}

/* Tables */
.table-container {
  background-color: var(--color-bg-card);
  border-radius: var(--radius-md);
  border: 1px solid var(--color-border);
  box-shadow: var(--shadow-xs);
  overflow-x: auto;
  overflow-y: auto;
  max-height: 560px;
  margin-bottom: 24px;
  position: relative;
  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 #f8fafc;
}

/* Custom sleek scrollbar for table container */
.table-container::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.table-container::-webkit-scrollbar-track {
  background: #f8fafc;
  border-radius: 4px;
}

.table-container::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 4px;
}

.table-container::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

/* Sleek custom scrollbar utility for scrollable cards and lists */
.card-scroll-pane {
  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 #f8fafc;
}
.card-scroll-pane::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}
.card-scroll-pane::-webkit-scrollbar-track {
  background: #f8fafc;
  border-radius: 4px;
}
.card-scroll-pane::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 4px;
}
.card-scroll-pane::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  text-align: left;
  font-size: 13px;
}

.data-table th,
.table-container table th {
  position: sticky;
  top: 0;
  z-index: 5;
  background-color: #f8fafc;
  color: #475569;
  font-weight: 600;
  padding: 12px 16px;
  border-bottom: 1px solid var(--color-border);
  box-shadow: inset 0 -1px 0 var(--color-border);
  text-transform: uppercase;
  font-size: 11px;
  letter-spacing: 0.5px;
}

.data-table td {
  padding: 14px 16px;
  border-bottom: 1px solid #f1f5f9;
  color: var(--color-text-main);
  vertical-align: middle;
}

.data-table tr:last-child td {
  border-bottom: none;
}

.data-table tr:hover td {
  background-color: #f8fafc;
}

/* Table Pagination Bar */
.table-pagination-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  padding: 12px 18px;
  background-color: #ffffff;
  border-top: 1px solid var(--color-border);
  font-size: 13px;
  color: #64748b;
  border-bottom-left-radius: var(--radius-md);
  border-bottom-right-radius: var(--radius-md);
}

.table-pagination-info {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.table-pagination-count {
  font-size: 12px;
  color: #64748b;
}

.table-pagination-count strong {
  color: #1e293b;
  font-weight: 600;
}

.table-pagination-size-select {
  padding: 4px 8px;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  font-size: 12px;
  background-color: #ffffff;
  color: #1e293b;
  cursor: pointer;
  outline: none;
}

.table-pagination-size-select:focus {
  border-color: #0284c7;
  box-shadow: 0 0 0 2px rgba(2, 132, 199, 0.15);
}

.table-pagination-nav {
  display: flex;
  align-items: center;
  gap: 4px;
}

.table-pagination-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 30px;
  height: 30px;
  padding: 0 8px;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
  background: #ffffff;
  color: #475569;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;
  user-select: none;
  line-height: 1;
}

.table-pagination-btn:hover:not(:disabled) {
  background-color: #f1f5f9;
  border-color: #cbd5e1;
  color: #0f172a;
}

.table-pagination-btn.active {
  background-color: #0284c7;
  border-color: #0284c7;
  color: #ffffff;
  font-weight: 600;
}

.table-pagination-btn:disabled {
  opacity: 0.35;
  cursor: not-allowed;
  background-color: #f8fafc;
}

.table-pagination-ellipsis {
  padding: 0 4px;
  color: #94a3b8;
  font-size: 13px;
}

/* Badges */
.badge-pill {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: var(--radius-full);
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.3px;
}

.badge-info { background-color: #eff6ff; color: #2563eb; }
.badge-success { background-color: #ecfdf5; color: #059669; }
.badge-warning { background-color: #fffbeb; color: #d97706; }
.badge-danger { background-color: #fef2f2; color: #dc2626; }
.badge-neutral { background-color: #f1f5f9; color: #475569; }

/* Modals */
.modal-backdrop {
  position: fixed;
  inset: 0;
  background-color: rgba(15, 23, 42, 0.5);
  backdrop-filter: blur(3px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.modal-card {
  background-color: #ffffff;
  border-radius: var(--radius-lg);
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  border: 1px solid var(--color-border);
  width: 100%;
  max-width: 680px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.modal-card.modal-sm { max-width: 480px; }
.modal-card.modal-md { max-width: 680px; }
.modal-card.modal-lg { max-width: 860px; }
.modal-card.modal-xl { max-width: 1060px; }

/* Sizing helpers */
.max-w-sm { max-width: 480px !important; }
.max-w-md { max-width: 560px !important; }
.max-w-lg { max-width: 680px !important; }
.max-w-xl { max-width: 780px !important; }
.max-w-2xl { max-width: 840px !important; }
.max-w-3xl { max-width: 920px !important; }
.max-w-4xl { max-width: 1020px !important; }
.max-w-5xl { max-width: 1140px !important; }

.modal-header {
  padding: 20px 28px;
  border-bottom: 1px solid var(--color-border);
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  background-color: #ffffff;
  flex-shrink: 0;
}

.modal-header > div:first-child,
.modal-header-content {
  flex: 1;
  min-width: 0;
}

.modal-header h1,
.modal-header h2,
.modal-header h3 {
  font-size: 19px;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
  line-height: 1.35;
}

.modal-header p {
  margin: 4px 0 0 0;
  font-size: 13px;
  color: #64748b;
  line-height: 1.45;
}

.modal-close-btn,
.close-btn {
  background: none;
  border: none;
  font-size: 18px;
  cursor: pointer;
  color: #94a3b8;
  padding: 6px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;
  line-height: 1;
  flex-shrink: 0;
}

.modal-close-btn:hover,
.close-btn:hover {
  background-color: #f1f5f9;
  color: #0f172a;
}

.modal-body {
  padding: 24px 28px;
  overflow-y: auto;
  flex-grow: 1;
}

.modal-footer {
  padding: 16px 28px;
  border-top: 1px solid var(--color-border);
  background-color: #f8fafc;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  flex-shrink: 0;
}

/* Fallback utilities for components with utility classes */
.fixed { position: fixed; }
.inset-0 { top: 0; right: 0; bottom: 0; left: 0; }
.z-50 { z-index: 1000; }
.z-60 { z-index: 1050; }
.flex { display: flex; }
.flex-1 { flex: 1 1 0%; }
.flex-col { flex-direction: column; }
.flex-wrap { flex-wrap: wrap; }
.items-center { align-items: center; }
.items-start { align-items: flex-start; }
.justify-between { justify-content: space-between; }
.justify-end { justify-content: flex-end; }
.justify-center { justify-content: center; }
.gap-1 { gap: 4px; }
.gap-2 { gap: 8px; }
.gap-3 { gap: 12px; }
.gap-4 { gap: 16px; }
.gap-6 { gap: 24px; }
.grid { display: grid; }
.grid-cols-2 { grid-template-columns: repeat(2, minmax(0, 1fr)); }
.grid-cols-3 { grid-template-columns: repeat(3, minmax(0, 1fr)); }
.grid-cols-4 { grid-template-columns: repeat(4, minmax(0, 1fr)); }

.space-y-1 > * + * { margin-top: 4px; }
.space-y-2 > * + * { margin-top: 8px; }
.space-y-3 > * + * { margin-top: 12px; }
.space-y-4 > * + * { margin-top: 16px; }
.space-y-5 > * + * { margin-top: 20px; }
.space-y-6 > * + * { margin-top: 24px; }
.space-x-2 > * + * { margin-left: 8px; }
.space-x-3 > * + * { margin-left: 12px; }
.space-x-4 > * + * { margin-left: 16px; }
.space-x-6 > * + * { margin-left: 24px; }

/* Spacing & Padding */
.p-2 { padding: 8px; }
.p-2\.5 { padding: 10px; }
.p-3 { padding: 12px; }
.p-4 { padding: 16px; }
.p-5 { padding: 20px; }
.p-6 { padding: 24px; }
.px-2 { padding-left: 8px; padding-right: 8px; }
.px-3 { padding-left: 12px; padding-right: 12px; }
.px-4 { padding-left: 16px; padding-right: 16px; }
.px-5 { padding-left: 20px; padding-right: 20px; }
.px-6 { padding-left: 24px; padding-right: 24px; }
.py-0\.5 { padding-top: 2px; padding-bottom: 2px; }
.py-1 { padding-top: 4px; padding-bottom: 4px; }
.py-1\.5 { padding-top: 6px; padding-bottom: 6px; }
.py-2 { padding-top: 8px; padding-bottom: 8px; }
.py-3 { padding-top: 12px; padding-bottom: 12px; }
.mt-0\.5 { margin-top: 2px; }
.mt-1 { margin-top: 4px; }
.mt-2 { margin-top: 8px; }
.mt-3 { margin-top: 12px; }
.mt-4 { margin-top: 16px; }
.mb-1 { margin-bottom: 4px; }
.mb-2 { margin-bottom: 8px; }
.mb-3 { margin-bottom: 12px; }
.mb-4 { margin-bottom: 16px; }

/* Backgrounds & Colors */
.bg-white { background-color: #ffffff; }
.bg-gray-50 { background-color: #f8fafc; }
.bg-gray-50\/30 { background-color: rgba(248, 250, 252, 0.4); }
.bg-gray-100 { background-color: #f1f5f9; }
.bg-gray-200 { background-color: #e2e8f0; }
.bg-blue-50 { background-color: #eff6ff; }
.bg-blue-100 { background-color: #dbeafe; }
.bg-blue-600 { background-color: #2563eb; color: #ffffff; }
.bg-blue-700 { background-color: #1d4ed8; color: #ffffff; }
.bg-indigo-600 { background-color: #4f46e5; color: #ffffff; }
.bg-yellow-600 { background-color: #d97706; color: #ffffff; }
.bg-orange-600 { background-color: #ea580c; color: #ffffff; }
.bg-purple-50 { background-color: #faf5ff; }
.bg-purple-50\/30 { background-color: rgba(250, 245, 255, 0.4); }
.bg-purple-100 { background-color: #f3e8ff; }
.bg-purple-600 { background-color: #9333ea; color: #ffffff; }
.bg-emerald-50 { background-color: #ecfdf5; }
.bg-emerald-100 { background-color: #d1fae5; }
.bg-emerald-600 { background-color: #059669; color: #ffffff; }
.bg-teal-50\/20 { background-color: rgba(240, 253, 250, 0.3); }
.bg-teal-100 { background-color: #ccfbf1; }
.bg-teal-600 { background-color: #0d9488; color: #ffffff; }
.bg-amber-50 { background-color: #fffbeb; }
.bg-amber-50\/50 { background-color: rgba(255, 251, 235, 0.5); }
.bg-amber-50\/60 { background-color: rgba(255, 251, 235, 0.6); }
.bg-amber-100 { background-color: #fef3c7; }
.bg-red-50 { background-color: #fef2f2; }
.bg-red-100 { background-color: #fee2e2; }
.bg-red-600 { background-color: #dc2626; color: #ffffff; }

/* Text colors */
.text-white { color: #ffffff; }
.text-gray-400 { color: #94a3b8; }
.text-gray-500 { color: #64748b; }
.text-gray-600 { color: #475569; }
.text-gray-700 { color: #334155; }
.text-gray-800 { color: #1e293b; }
.text-gray-900 { color: #0f172a; }
.text-blue-600 { color: #2563eb; }
.text-blue-700 { color: #1d4ed8; }
.text-purple-700 { color: #7e22ce; }
.text-purple-800 { color: #6b21a8; }
.text-purple-900 { color: #581c87; }
.text-emerald-800 { color: #065f46; }
.text-teal-800 { color: #115e59; }
.text-teal-900 { color: #134e4a; }
.text-amber-800 { color: #92400e; }
.text-red-700 { color: #b91c1c; }
.text-red-800 { color: #991b1b; }

/* Typography */
.text-\[11px\] { font-size: 11px; }
.text-xs { font-size: 12px; }
.text-sm { font-size: 14px; }
.text-base { font-size: 16px; }
.font-medium { font-weight: 500; }
.font-semibold { font-weight: 600; }
.font-bold { font-weight: 700; }
.font-mono { font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace; }
.uppercase { text-transform: uppercase; }
.tracking-wide { letter-spacing: 0.025em; }
.tracking-wider { letter-spacing: 0.05em; }
.text-center { text-align: center; }

/* Borders & Radius */
.border { border: 1px solid #e2e8f0; }
.border-b { border-bottom: 1px solid #e2e8f0; }
.border-t { border-top: 1px solid #e2e8f0; }
.border-b-2 { border-bottom-width: 2px; border-bottom-style: solid; }
.border-transparent { border-color: transparent; }
.border-dashed { border-style: dashed; }
.border-gray-100 { border-color: #f1f5f9; }
.border-gray-200 { border-color: #e2e8f0; }
.border-gray-300 { border-color: #cbd5e1; }
.border-blue-100 { border-color: #dbeafe; }
.border-blue-200 { border-color: #bfdbfe; }
.border-emerald-200 { border-color: #a7f3d0; }
.border-purple-100 { border-color: #f3e8ff; }
.border-purple-200 { border-color: #e9d5ff; }
.border-amber-200 { border-color: #fde68a; }
.border-teal-200 { border-color: #99f6e4; }
.border-red-200 { border-color: #fecaca; }
.border-red-300 { border-color: #fca5a5; }
.border-slate-200 { border-color: #e2e8f0; }
.last\:border-none:last-child { border: none; }
.last\:pb-0:last-child { padding-bottom: 0; }
.rounded { border-radius: 6px; }
.rounded-lg { border-radius: 8px; }
.rounded-xl { border-radius: 12px; }
.rounded-full { border-radius: 9999px; }
.overflow-hidden { overflow: hidden; }
.overflow-y-auto { overflow-y: auto; }
.shadow-xs { box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05); }
.shadow-sm { box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1); }
.shadow-inner { box-shadow: inset 0 2px 4px 0 rgba(0, 0, 0, 0.06); }
.block { display: block; }
.inline-block { display: inline-block; }
.inline-flex { display: inline-flex; }
.hidden { display: none; }
.shrink-0 { flex-shrink: 0; }
.italic { font-style: italic; }
.whitespace-pre-line { whitespace: pre-line; }
.leading-relaxed { line-height: 1.625; }
.ml-2 { margin-left: 8px; }
.mr-2 { margin-right: 8px; }
.mx-6 { margin-left: 24px; margin-right: 24px; }
.mx-auto { margin-left: auto; margin-right: auto; }
.mb-0\.5 { margin-bottom: 2px; }
.gap-1\.5 { gap: 6px; }
.p-1\.5 { padding: 6px; }
.p-8 { padding: 32px; }
.pb-2 { padding-bottom: 8px; }
.pb-3 { padding-bottom: 12px; }
.pt-1 { padding-top: 4px; }
.pt-2 { padding-top: 8px; }
.pt-3 { padding-top: 12px; }
.w-5 { width: 20px; }
.h-5 { height: 20px; }
.w-24 { width: 96px; }
.h-3 { height: 12px; }
.w-full { width: 100%; }
.max-w-md { max-width: 28rem; }
.max-w-lg { max-width: 32rem; }
.max-w-7xl { max-width: 80rem; }
.grid-cols-1 { grid-template-columns: repeat(1, minmax(0, 1fr)); }
.text-\[10px\] { font-size: 10px; }
.text-xl { font-size: 20px; }
.text-right { text-align: right; }
.text-amber-600 { color: #d97706; }
.text-amber-700 { color: #b45309; }
.text-amber-900 { color: #78350f; }
.text-blue-800 { color: #1e40af; }
.text-blue-900 { color: #1e3a8a; }
.text-emerald-600 { color: #059669; }
.text-emerald-700 { color: #047857; }
.text-green-600 { color: #16a34a; }
.text-slate-700 { color: #334155; }
.text-slate-900 { color: #0f172a; }
.font-extrabold { font-weight: 800; }
.bg-blue-50\/50 { background-color: rgba(239, 246, 255, 0.5); }
.bg-emerald-50\/50 { background-color: rgba(236, 253, 245, 0.5); }
.bg-purple-50\/50 { background-color: rgba(250, 245, 255, 0.5); }
.bg-rose-600 { background-color: #e11d48; color: #ffffff; }
.bg-slate-50 { background-color: #f8fafc; }
.bg-slate-800 { background-color: #1e293b; color: #ffffff; }

.transition { transition: all 0.15s ease; }
.transition-colors { transition: color 0.15s ease, background-color 0.15s ease, border-color 0.15s ease; }

/* Keyframe animations */
@keyframes bounce {
  0%, 100% { transform: translateY(-15%); animation-timing-function: cubic-bezier(0.8,0,1,1); }
  50% { transform: none; animation-timing-function: cubic-bezier(0,0,0.2,1); }
}
.animate-bounce { animation: bounce 1s infinite; }

/* Hover utilities */
.hover\:bg-blue-700:hover { background-color: #1d4ed8; }
.hover\:bg-indigo-700:hover { background-color: #4338ca; }
.hover\:bg-yellow-700:hover { background-color: #b45309; }
.hover\:bg-orange-700:hover { background-color: #c2410c; }
.hover\:bg-purple-100:hover { background-color: #f3e8ff; }
.hover\:bg-purple-700:hover { background-color: #7e22ce; }
.hover\:bg-emerald-700:hover { background-color: #047857; }
.hover\:bg-teal-700:hover { background-color: #0f766e; }
.hover\:bg-red-200:hover { background-color: #fecaca; }
.hover\:bg-red-700:hover { background-color: #b91c1c; }
.hover\:bg-rose-700:hover { background-color: #be123c; }
.hover\:bg-black:hover { background-color: #000000; }
.hover\:bg-gray-200:hover { background-color: #e2e8f0; }
.hover\:text-gray-700:hover { color: #334155; }

/* Focus styles */
.focus\:outline-hidden:focus { outline: none; }
.focus\:ring-2:focus { box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.5); }
.focus\:ring-blue-500:focus { border-color: #3b82f6; }

/* Responsive grid and flex */
@media (min-width: 768px) {
  .md\:flex-row { flex-direction: row; }
  .md\:items-center { align-items: center; }
  .md\:items-start { align-items: flex-start; }
  .md\:grid-cols-2 { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .md\:grid-cols-3 { grid-template-columns: repeat(3, minmax(0, 1fr)); }
  .md\:grid-cols-4 { grid-template-columns: repeat(4, minmax(0, 1fr)); }
}

/* Common tab button style inside modal */
.modal-card .border-b-2 {
  background: transparent;
  border-top: none;
  border-left: none;
  border-right: none;
  cursor: pointer;
  outline: none;
}
.modal-card .border-b-2:hover {
  color: var(--color-primary);
}

/* Modal action & control buttons */
.modal-card button {
  font-family: inherit;
  cursor: pointer;
}
.modal-card button[class*="px-"] {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid transparent;
  cursor: pointer;
  transition: all 0.15s ease;
}
.modal-card button[class*="px-"]:hover {
  filter: brightness(0.95);
}
.modal-card button[class*="border"] {
  border-style: solid;
}
.modal-card button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Form input elements in modal */
.modal-card select,
.modal-card input[type="text"],
.modal-card input[type="date"],
.modal-card textarea {
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  padding: 6px 12px;
  font-family: inherit;
  font-size: 13px;
  background-color: #ffffff;
  color: #1e293b;
  outline: none;
}
.modal-card select:focus,
.modal-card input[type="text"]:focus,
.modal-card input[type="date"]:focus,
.modal-card textarea:focus {
  border-color: #3b82f6;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
}


/* Login Screen Styles */
.login-page-wrapper {
  min-height: 100vh;
  width: 100vw;
  background-color: #f1f5f9;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}

.login-card {
  width: 100%;
  max-width: 440px;
  background: #ffffff;
  border-radius: var(--radius-lg);
  border: 1px solid var(--color-border);
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.05), 0 8px 10px -6px rgba(0, 0, 0, 0.03);
  padding: 40px 36px;
}

.login-brand {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-bottom: 24px;
}

.login-brand-icon {
  background-color: var(--color-primary);
  color: #ffffff;
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.login-brand-img {
  width: 44px;
  height: 44px;
  object-fit: contain;
  border-radius: 10px;
  flex-shrink: 0;
}

.login-brand-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--color-text-main);
  letter-spacing: -0.02em;
}

.login-header-text {
  text-align: center;
  margin-bottom: 32px;
}

.login-header-text h2 {
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--color-text-main);
  margin-bottom: 6px;
}

.login-header-text p {
  font-size: 0.875rem;
  color: var(--color-text-muted);
}

.login-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.form-field label {
  font-size: 0.8125rem;
  font-weight: 600;
  color: #334155;
}

.form-field select,
.form-field input,
.form-field textarea {
  width: 100%;
  padding: 11px 14px;
  border-radius: var(--radius-sm);
  border: 1px solid var(--color-border);
  background-color: #ffffff;
  font-size: 0.875rem;
  color: var(--color-text-main);
  font-family: inherit;
  transition: all 0.15s ease;
}

.form-field select:focus,
.form-field input:focus,
.form-field textarea:focus {
  outline: none;
  border-color: var(--color-primary);
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
}

.form-grid-2 {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.form-grid-3 {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 16px;
}

@media (max-width: 640px) {
  .form-grid-2,
  .form-grid-3 {
    grid-template-columns: 1fr;
  }
}

.quick-login-pills {
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid var(--color-border);
}

.quick-login-pills p {
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--color-text-muted);
  margin-bottom: 10px;
  text-align: center;
}

.pill-group {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  justify-content: center;
}

.pill-btn {
  background-color: #f8fafc;
  border: 1px solid var(--color-border);
  color: var(--color-text-main);
  font-size: 0.75rem;
  padding: 5px 10px;
  border-radius: var(--radius-full);
  cursor: pointer;
  transition: all 0.15s;
}

.pill-btn:hover {
  background-color: var(--color-primary-light);
  border-color: var(--color-primary);
  color: var(--color-primary);
}

.login-error-alert {
  background-color: #fef2f2;
  border: 1px solid #fecaca;
  color: #b91c1c;
  padding: 10px 14px;
  border-radius: var(--radius-sm);
  font-size: 0.8125rem;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
}

/* ==========================================================================
   21. RESPONSIVE DESIGN (TABLET & MOBILE)
   ========================================================================== */

/* Base Navigation Drawer & Mobile Elements */
.mobile-menu-toggle-btn {
  display: none;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border-radius: 9px;
  background-color: #f1f5f9;
  border: 1px solid #e2e8f0;
  color: #0f172a;
  cursor: pointer;
  flex-shrink: 0;
  transition: all 0.15s ease;
  padding: 0;
}
.mobile-menu-toggle-btn:hover {
  background-color: #e2e8f0;
  color: #2563eb;
}

.mobile-header-brand {
  display: none;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  flex-shrink: 0;
}
.mobile-header-logo {
  width: 28px;
  height: 28px;
  object-fit: contain;
  border-radius: 6px;
}
.mobile-header-title {
  font-size: 16px;
  font-weight: 800;
  letter-spacing: 0.08em;
  color: #0f172a;
}

.mobile-sidebar-close-btn {
  display: none;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: none;
  background-color: #f1f5f9;
  color: #475569;
  cursor: pointer;
  flex-shrink: 0;
}
.mobile-sidebar-close-btn:hover {
  background-color: #fee2e2;
  color: #dc2626;
}

.mobile-sidebar-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.55);
  backdrop-filter: blur(2px);
  z-index: 1040;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.25s ease;
}
.mobile-sidebar-backdrop.open {
  opacity: 1;
  pointer-events: auto;
}

/* Tablets & Smaller Desktops (max-width: 1024px) */
@media (max-width: 1024px) {
  /* 1. Mobile Drawer Navigation */
  .sidebar-wrapper {
    width: 0 !important;
    min-width: 0 !important;
    flex: 0 0 0 !important;
    overflow: visible !important;
  }

  .sidebar-edge-toggle {
    display: none !important;
  }

  .sidebar {
    position: fixed !important;
    top: 0 !important;
    left: 0 !important;
    bottom: 0 !important;
    width: min(320px, 86vw) !important;
    max-width: 86vw !important;
    height: 100vh !important;
    height: 100dvh !important;
    z-index: 1050 !important;
    transform: translateX(-100%) !important;
    transition: transform 0.28s cubic-bezier(0.32, 0.72, 0, 1), box-shadow 0.28s ease !important;
    box-shadow: none !important;
    background-color: #ffffff !important;
    padding: 16px 12px !important;
    display: flex !important;
    flex-direction: column !important;
  }

  .sidebar.mobile-open {
    transform: translateX(0) !important;
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.3), 0 8px 10px -6px rgba(0, 0, 0, 0.2) !important;
  }

  /* Force expanded content inside mobile drawer regardless of desktop collapsed state */
  .sidebar-brand-header {
    display: flex !important;
    justify-content: space-between !important;
    align-items: center !important;
    width: 100% !important;
    margin-bottom: 18px !important;
    padding: 0 4px !important;
  }

  .sidebar.collapsed .sidebar-brand-header,
  .sidebar.collapsed .sidebar-brand-main {
    justify-content: flex-start !important;
    flex: 1 !important;
  }

  .sidebar.collapsed .nav-item {
    justify-content: flex-start !important;
    padding: 9px 12px !important;
  }

  .sidebar.collapsed .sidebar-accordion-header {
    justify-content: space-between !important;
    padding: 9px 10px !important;
  }

  .sidebar.collapsed .sidebar-accordion-items {
    display: flex !important;
  }

  .sidebar.collapsed .sidebar-footer {
    justify-content: space-between !important;
    padding: 12px 6px 0 6px !important;
  }

  .mobile-sidebar-close-btn {
    display: inline-flex !important;
  }

  .mobile-menu-toggle-btn {
    display: inline-flex !important;
  }

  /* 2. Main Content & Layout */
  .main-content {
    width: 100% !important;
    max-width: 100vw !important;
    flex: 1 1 100% !important;
    min-width: 0 !important;
  }

  .page-container {
    padding: 18px 20px 40px 20px !important;
    max-width: 100vw !important;
    box-sizing: border-box !important;
  }

  .search-bar {
    width: 220px;
  }

  .module-grid {
    grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
    gap: 16px;
  }

  .header-org-badge {
    max-width: 160px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  /* 3. Universal Tables Responsiveness (Tablets & Mobiles) */
  .table-container,
  .table-responsive,
  .dash-table-wrap,
  .dash-ind-modal-table-wrap {
    width: 100% !important;
    max-width: 100% !important;
    overflow-x: auto !important;
    -webkit-overflow-scrolling: touch !important;
    display: block !important;
  }

  .data-table,
  .table-container > table,
  .table-responsive > table {
    min-width: 680px !important;
  }

  .dash-table {
    min-width: 560px !important;
  }

  /* 4. Universal Navigation Tabs */
  .nav-tabs,
  .profile-nav-tabs,
  .tabs-header {
    display: flex !important;
    flex-wrap: nowrap !important;
    overflow-x: auto !important;
    -webkit-overflow-scrolling: touch !important;
    scrollbar-width: none !important;
    width: 100% !important;
    max-width: 100% !important;
    margin-bottom: 16px !important;
    padding-bottom: 4px !important;
  }

  .nav-tabs::-webkit-scrollbar,
  .profile-nav-tabs::-webkit-scrollbar,
  .tabs-header::-webkit-scrollbar {
    display: none !important;
  }

  .tab-btn,
  .profile-nav-tab {
    flex-shrink: 0 !important;
    white-space: nowrap !important;
  }

  /* 5. Metrics & Grid Stacking */
  .stats-grid,
  .dash-metrics-grid,
  .kpi-grid,
  .stat-cards-grid {
    grid-template-columns: repeat(2, 1fr) !important;
    gap: 12px !important;
  }

  .dash-tables-grid {
    grid-template-columns: 1fr !important;
    gap: 16px !important;
  }

  .cal-modal-body {
    grid-template-columns: 1fr !important;
    overflow-y: auto !important;
  }
}

/* Mobile Devices & Phones (max-width: 768px) */
@media (max-width: 768px) {
  /* 1. Viewport & Body Protection */
  .app-container {
    position: relative;
    width: 100vw;
    height: 100vh;
    height: 100dvh;
    overflow: hidden;
  }

  .page-container {
    padding: 14px 12px 32px 12px !important;
    -webkit-overflow-scrolling: touch;
    max-width: 100vw !important;
    box-sizing: border-box !important;
  }

  /* 2. Top Header on Mobile */
  .top-header {
    height: 54px !important;
    padding: 0 10px !important;
    justify-content: space-between !important;
    gap: 8px !important;
  }

  .header-left {
    gap: 8px !important;
    flex: 1 1 auto !important;
    min-width: 0 !important;
  }

  .header-right {
    gap: 8px !important;
    flex-shrink: 0 !important;
  }

  .mobile-header-brand {
    display: inline-flex !important;
  }

  .search-bar {
    display: none !important; /* Hide large search bar on phones */
  }

  .header-org-badge {
    display: none !important;
  }

  .header-date-pill-btn {
    display: none !important; /* Hide date pill on small screens to save space */
  }

  .btn-submit-report {
    padding: 6px 12px !important;
    font-size: 12px !important;
    gap: 6px !important;
    border-radius: var(--radius-full);
  }

  /* Profile Menu on Mobile */
  .profile-trigger-btn {
    padding: 2px !important;
    border: none !important;
    background: transparent !important;
  }

  .profile-info-text {
    display: none !important; /* Hide text name and role; show circular avatar only */
  }

  .profile-chevron {
    display: none !important;
  }

  .profile-avatar {
    width: 34px !important;
    height: 34px !important;
  }

  .header-logout-btn {
    width: 34px !important;
    height: 34px !important;
    padding: 0 !important;
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
  }

  .header-notif-btn {
    width: 34px !important;
    height: 34px !important;
  }

  /* 3. Floating Dropdowns - Anchor safely within viewport */
  .notif-dropdown {
    position: fixed !important;
    top: 58px !important;
    left: 8px !important;
    right: 8px !important;
    width: auto !important;
    max-width: 420px !important;
    margin: 0 auto !important;
    max-height: 80vh !important;
    z-index: 1060 !important;
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.25) !important;
  }

  .profile-dropdown-card {
    position: fixed !important;
    top: 58px !important;
    right: 8px !important;
    left: auto !important;
    width: calc(100vw - 16px) !important;
    max-width: 320px !important;
    max-height: 85vh !important;
    overflow-y: auto !important;
    z-index: 1060 !important;
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.25) !important;
  }

  /* 4. Page Banners & Headers */
  .page-header-banner {
    padding: 14px 16px !important;
    margin-bottom: 14px !important;
    border-radius: var(--radius-md) !important;
    flex-direction: column !important;
    align-items: flex-start !important;
    gap: 12px !important;
  }

  .page-header-banner h1 {
    font-size: 1.15rem !important;
    line-height: 1.3 !important;
  }

  .page-header-banner p {
    font-size: 0.8125rem !important;
  }

  .module-grid {
    grid-template-columns: 1fr !important;
    gap: 12px !important;
  }

  .module-card {
    padding: 14px !important;
    gap: 12px !important;
  }

  .stat-card {
    padding: 12px 10px !important;
  }

  .stat-card .stat-value {
    font-size: 18px !important;
  }

  .tab-btn {
    padding: 8px 12px !important;
    font-size: 13px !important;
  }

  /* 5. Modals & Forms on Mobile */
  .modal-backdrop,
  .submit-modal-backdrop,
  .cal-modal-backdrop {
    padding: 8px !important;
    align-items: center !important;
    justify-content: center !important;
  }

  .modal-card,
  .submit-modal-card,
  .cal-modal-container,
  .report-modal-card {
    width: 100% !important;
    max-width: 96vw !important;
    max-height: 94vh !important;
    border-radius: 14px !important;
    padding: 14px !important;
    box-sizing: border-box !important;
  }

  .modal-header,
  .cal-modal-header,
  .submit-modal-header {
    padding: 12px 14px !important;
  }

  .modal-body,
  .submit-modal-body {
    padding: 12px 14px !important;
  }

  .modal-footer,
  .submit-modal-footer {
    padding: 12px 14px !important;
    flex-direction: column-reverse !important;
    gap: 8px !important;
  }

  .modal-footer button,
  .submit-modal-footer button {
    width: 100% !important;
  }

  /* Stack multi-column form grids into a clean single column on mobile */
  .submit-form-fields-grid,
  .form-grid-2,
  .form-grid-3,
  .modal-body .grid {
    grid-template-columns: 1fr !important;
    gap: 10px !important;
  }

  /* 6. Table Cell Spacing & Pagination */
  .data-table th,
  .data-table td {
    padding: 8px 10px !important;
    font-size: 12px !important;
  }

  .table-pagination-container {
    flex-direction: column !important;
    align-items: stretch !important;
    gap: 10px !important;
    padding: 10px 12px !important;
  }

  .table-pagination-info,
  .table-pagination-nav {
    justify-content: space-between !important;
  }

  /* 7. Input and Button Touch Targets (Prevent iOS Safari auto-zoom) */
  input[type="text"],
  input[type="search"],
  input[type="email"],
  input[type="date"],
  input[type="datetime-local"],
  select,
  textarea {
    font-size: 14px !important;
  }
}

/* Very Small Mobile Screens (max-width: 480px) */
@media (max-width: 480px) {
  .btn-submit-report .btn-submit-text {
    display: none !important; /* Hide text, keep sleek Send icon button */
  }

  .btn-submit-report {
    padding: 6px 10px !important;
    border-radius: 50% !important;
    width: 34px !important;
    height: 34px !important;
    justify-content: center !important;
  }

  .mobile-header-title {
    font-size: 14px !important;
  }

  .stats-grid,
  .dash-metrics-grid,
  .kpi-grid,
  .stat-cards-grid {
    grid-template-columns: 1fr !important;
  }

  .submit-kind-grid {
    grid-template-columns: 1fr !important;
  }

  .dash-card {
    padding: 14px 12px !important;
    border-radius: 12px !important;
  }

  .dash-card-header {
    flex-wrap: wrap !important;
    gap: 8px !important;
  }

  .dash-table td,
  .dash-table th {
    padding: 8px 10px !important;
    font-size: 12px !important;
  }

  .profile-settings-card {
    padding: 14px 12px !important;
  }
}


/* USER PROFILE MENU COMPONENT                                               */
/* ========================================================================= */
.profile-menu-wrapper {
  position: relative;
}

.profile-trigger-btn {
  background: transparent;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 4px 8px;
  border-radius: var(--radius-sm);
  transition: all 0.15s ease;
  font-family: inherit;
}

.profile-trigger-btn:hover {
  background-color: #f1f5f9;
}

.profile-avatar {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: linear-gradient(135deg, #2563eb 0%, #1e40af 100%);
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 13px;
  box-shadow: 0 2px 4px rgba(37, 99, 235, 0.2);
  flex-shrink: 0;
}

.profile-info-text {
  display: flex;
  flex-direction: column;
  text-align: left;
}

.profile-name {
  font-size: 13px;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.2;
}

.profile-role {
  font-size: 11px;
  font-weight: 500;
  color: #64748b;
  line-height: 1.2;
  margin-top: 2px;
}

.profile-chevron {
  color: #94a3b8;
  transition: transform 0.2s ease;
}

.profile-chevron.open {
  transform: rotate(180deg);
}

.profile-backdrop {
  position: fixed;
  inset: 0;
  z-index: 100;
}

.profile-dropdown-card {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  width: 250px;
  background-color: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.05);
  padding: 12px;
  z-index: 101;
  animation: fadeInDown 0.15s ease-out;
}

.profile-dropdown-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding-bottom: 12px;
}

.profile-dropdown-avatar {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: linear-gradient(135deg, #2563eb 0%, #1e40af 100%);
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
  font-weight: 700;
  flex-shrink: 0;
}

.profile-dropdown-meta {
  min-width: 0;
  flex: 1;
}

.profile-dropdown-name {
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
}

.profile-dropdown-email {
  font-size: 11px;
  color: #64748b;
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.profile-dropdown-org {
  font-size: 11px;
  color: #64748b;
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 2px;
}

.profile-role-card {
  background-color: #eff6ff;
  border: 1px solid #dbeafe;
  border-radius: 8px;
  padding: 8px 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 6px;
}

.profile-role-left {
  display: flex;
  align-items: center;
  gap: 6px;
}

.profile-role-title {
  font-size: 11px;
  font-weight: 600;
  color: #1e40af;
}

.profile-role-badge {
  font-size: 11px;
  font-weight: 700;
  color: #1d4ed8;
  background-color: #ffffff;
  padding: 2px 8px;
  border-radius: 6px;
  border: 1px solid #bfdbfe;
}

.profile-dropdown-divider {
  height: 1px;
  background-color: #f1f5f9;
  margin: 10px 0;
}

.profile-dropdown-actions {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.profile-dropdown-item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 8px 10px;
  border-radius: 8px;
  background: transparent;
  border: none;
  font-size: 13px;
  font-weight: 500;
  color: #334155;
  cursor: pointer;
  text-align: left;
  transition: all 0.15s ease;
  font-family: inherit;
}

.profile-dropdown-item:hover {
  background-color: #f8fafc;
  color: #0f172a;
}

.profile-dropdown-item svg {
  color: #64748b;
}

.profile-logout-btn {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 8px 10px;
  border-radius: 8px;
  background: transparent;
  border: none;
  font-size: 13px;
  font-weight: 600;
  color: #dc2626;
  cursor: pointer;
  text-align: left;
  transition: all 0.15s ease;
  font-family: inherit;
}

.profile-logout-btn:hover {
  background-color: #fef2f2;
}

/* ========================================================================= */
/* CALENDAR DUE MODAL COMPONENT                                              */
/* ========================================================================= */
.cal-modal-backdrop {
  position: fixed;
  inset: 0;
  background-color: rgba(15, 23, 42, 0.45);
  backdrop-filter: blur(4px);
  z-index: 120;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  animation: fadeIn 0.15s ease-out;
}

.cal-modal-container {
  background-color: #ffffff;
  border-radius: 20px;
  max-width: 860px;
  width: 100%;
  max-height: 90vh;
  box-shadow: 0 25px 50px -12px rgba(15, 23, 42, 0.25);
  border: 1px solid #e2e8f0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: scaleUp 0.15s ease-out;
}

.cal-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid #f1f5f9;
}

.cal-modal-title-group {
  display: flex;
  align-items: center;
  gap: 14px;
}

.cal-modal-icon-wrap {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background-color: #f1f5f9;
  display: flex;
  align-items: center;
  justify-content: center;
}

.cal-modal-title {
  font-size: 17px;
  font-weight: 700;
  color: #0f172a;
}

.cal-modal-subtitle {
  font-size: 13px;
  color: #64748b;
  margin-top: 2px;
}

.cal-modal-close-btn {
  background: transparent;
  border: none;
  cursor: pointer;
  color: #94a3b8;
  padding: 6px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  transition: all 0.15s ease;
}

.cal-modal-close-btn:hover {
  background-color: #f1f5f9;
  color: #0f172a;
}

.cal-modal-body {
  display: grid;
  grid-template-columns: 360px 1fr;
  flex: 1;
  overflow: hidden;
}

/* Left Calendar Panel */
.cal-left-panel {
  padding: 20px 24px;
  border-right: 1px solid #f1f5f9;
  display: flex;
  flex-direction: column;
  background-color: #fafbfc;
}

.cal-month-nav {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}

.cal-current-month {
  font-size: 15px;
  font-weight: 700;
  color: #0f172a;
}

.cal-nav-buttons {
  display: flex;
  align-items: center;
  gap: 4px;
}

.cal-nav-arrow {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  color: #475569;
  border-radius: 6px;
  padding: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
}

.cal-nav-arrow:hover {
  background-color: #f1f5f9;
  color: #0f172a;
}

.cal-today-btn {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  color: #0f172a;
  border-radius: 6px;
  padding: 3px 8px;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
}

.cal-weekdays {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  text-align: center;
  margin-bottom: 8px;
}

.cal-weekday-cell {
  font-size: 11px;
  font-weight: 700;
  color: #94a3b8;
  text-transform: uppercase;
}

.cal-days-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 4px;
}

.cal-day-cell {
  height: 42px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border: 1px solid transparent;
  border-radius: 8px;
  background: transparent;
  cursor: pointer;
  position: relative;
  transition: all 0.12s ease;
  font-family: inherit;
}

.cal-day-cell:hover {
  background-color: #f1f5f9;
}

.cal-day-number {
  font-size: 13px;
  font-weight: 500;
  color: #334155;
}

.cal-day-today {
  border-color: #2563eb !important;
  background-color: #eff6ff !important;
}

.cal-day-today .cal-day-number {
  font-weight: 700;
  color: #2563eb;
}

.cal-day-selected {
  background-color: #0f172a !important;
}

.cal-day-selected .cal-day-number {
  color: #ffffff !important;
  font-weight: 700;
}

.cal-task-dots {
  display: flex;
  align-items: center;
  gap: 3px;
  position: absolute;
  bottom: 4px;
}

.cal-dot {
  width: 5px;
  height: 5px;
  border-radius: 50%;
}

.cal-dot-critical {
  background-color: #dc2626;
}

.cal-dot-high {
  background-color: #ea580c;
}

.cal-dot-blue {
  background-color: #2563eb;
}

.cal-legend {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid #e2e8f0;
}

.cal-legend-item {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  color: #64748b;
  font-weight: 500;
}

.cal-filter-notice {
  margin-top: 12px;
  padding: 6px 10px;
  border-radius: 6px;
  background-color: #e0f2fe;
  color: #0369a1;
  font-size: 11px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.cal-filter-clear {
  background: transparent;
  border: none;
  font-size: 11px;
  font-weight: 600;
  color: #0284c7;
  cursor: pointer;
  text-decoration: underline;
}

/* Right Panel: Task List */
.cal-right-panel {
  padding: 20px 24px;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  max-height: 540px;
}

.cal-type-filters {
  display: flex;
  gap: 6px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}

.cal-filter-pill {
  padding: 5px 12px;
  border-radius: var(--radius-full);
  background-color: #f1f5f9;
  border: 1px solid #e2e8f0;
  color: #475569;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
  font-family: inherit;
}

.cal-filter-pill:hover {
  background-color: #e2e8f0;
}

.cal-filter-pill.active {
  background-color: #0f172a;
  border-color: #0f172a;
  color: #ffffff;
}

.cal-tasks-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  overflow-y: auto;
}

.cal-tasks-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  color: #94a3b8;
  gap: 10px;
  font-size: 13px;
}

.cal-task-card {
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 12px 14px;
  background-color: #ffffff;
  cursor: pointer;
  transition: all 0.15s ease;
}

.cal-task-card:hover {
  border-color: #93c5fd;
  box-shadow: var(--shadow-sm);
  transform: translateY(-1px);
}

.cal-task-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
}

.cal-task-type-badge {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 700;
  color: #475569;
  text-transform: uppercase;
}

.cal-due-pill {
  font-size: 11px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 6px;
}

.cal-badge-critical {
  background-color: #fee2e2;
  color: #dc2626;
}

.cal-badge-high {
  background-color: #ffedd5;
  color: #ea580c;
}

.cal-badge-medium {
  background-color: #fef3c7;
  color: #d97706;
}

.cal-badge-normal {
  background-color: #eff6ff;
  color: #2563eb;
}

.cal-task-code {
  font-size: 12px;
  font-weight: 700;
  color: #0f172a;
  font-family: monospace;
}

.cal-task-title {
  font-size: 13px;
  font-weight: 600;
  color: #1e293b;
  margin: 3px 0 8px 0;
}

.cal-task-card-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-top: 1px solid #f8fafc;
  padding-top: 6px;
}

.cal-task-meta {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: #64748b;
}

.cal-task-status {
  font-weight: 600;
  color: #0f172a;
}

.cal-task-go-btn {
  color: #94a3b8;
  display: flex;
  align-items: center;
}

.cal-task-card:hover .cal-task-go-btn {
  color: #2563eb;
}

/* ========================================================================= */
/* NOTIFICATIONS POPOVER COMPONENT                                           */
/* ========================================================================= */
.notif-backdrop {
  position: fixed;
  inset: 0;
  z-index: 100;
}

.notif-dropdown {
  position: absolute;
  top: calc(var(--header-height) - 4px);
  right: 180px;
  width: 380px;
  background-color: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  box-shadow: var(--shadow-lg);
  z-index: 101;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: fadeInDown 0.15s ease-out;
}

.notif-header {
  padding: 14px 16px;
  border-bottom: 1px solid #f1f5f9;
  background-color: #fafbfc;
}

.notif-title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}

.notif-heading {
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
}

.notif-unread-badge {
  font-size: 11px;
  font-weight: 700;
  background-color: #ef4444;
  color: #ffffff;
  padding: 1px 7px;
  border-radius: var(--radius-full);
}

.notif-close-btn {
  background: transparent;
  border: none;
  cursor: pointer;
  color: #94a3b8;
  padding: 4px;
  border-radius: 6px;
  display: flex;
  align-items: center;
}

.notif-close-btn:hover {
  background-color: #f1f5f9;
  color: #0f172a;
}

.notif-sub-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.notif-filters {
  display: flex;
  gap: 4px;
}

.notif-filter-btn {
  background: transparent;
  border: none;
  padding: 3px 8px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
  cursor: pointer;
  font-family: inherit;
}

.notif-filter-btn:hover {
  color: #0f172a;
}

.notif-filter-btn.active {
  background-color: #0f172a;
  color: #ffffff;
}

.notif-mark-read-btn {
  background: transparent;
  border: none;
  font-size: 11px;
  font-weight: 600;
  color: #2563eb;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  font-family: inherit;
}

.notif-mark-read-btn:hover {
  text-decoration: underline;
}

.notif-list {
  max-height: 380px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}

.notif-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  color: #94a3b8;
  gap: 8px;
  font-size: 13px;
}

.notif-item {
  display: flex;
  gap: 12px;
  padding: 12px 16px;
  border-bottom: 1px solid #f8fafc;
  cursor: pointer;
  transition: background-color 0.12s ease;
}

.notif-item:hover {
  background-color: #f8fafc;
}

.notif-item.unread {
  background-color: #eff6ff;
}

.notif-item.unread:hover {
  background-color: #e0f2fe;
}

.notif-item-icon {
  margin-top: 2px;
  flex-shrink: 0;
}

.notif-item-content {
  flex: 1;
  min-width: 0;
}

.notif-item-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.notif-item-title {
  font-size: 12.5px;
  font-weight: 700;
  color: #0f172a;
}

.notif-item-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background-color: #2563eb;
  flex-shrink: 0;
}

.notif-item-desc {
  font-size: 12px;
  color: #475569;
  margin: 3px 0 6px 0;
  line-height: 1.4;
}

.notif-item-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 11px;
  color: #94a3b8;
}

.notif-item-category {
  font-weight: 700;
  color: #64748b;
}

.notif-footer {
  padding: 10px 16px;
  border-top: 1px solid #f1f5f9;
  background-color: #fafbfc;
  text-align: center;
}

.notif-footer-link {
  background: transparent;
  border: none;
  color: #2563eb;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-family: inherit;
}

.notif-footer-link:hover {
  text-decoration: underline;
}

/* ========================================================================= */
/* SUBMIT REPORT MULTI-STEP MODAL (4-STEP SMS INTAKE FLOW)                  */
/* ========================================================================= */
.submit-modal-backdrop {
  position: fixed;
  inset: 0;
  background-color: rgba(15, 23, 42, 0.45);
  backdrop-filter: blur(4px);
  z-index: 120;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  animation: fadeIn 0.15s ease-out;
}

.submit-modal-card {
  background-color: #ffffff;
  border-radius: 20px;
  box-shadow: 0 25px 50px -12px rgba(15, 23, 42, 0.25);
  border: 1px solid #e2e8f0;
  padding: 24px;
  animation: scaleUp 0.15s ease-out;
  display: flex;
  flex-direction: column;
}

.submit-modal-wide {
  max-width: 840px;
  width: 92%;
  max-height: 90vh;
}

.submit-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.submit-modal-close {
  background: transparent;
  border: none;
  cursor: pointer;
  color: #94a3b8;
  padding: 6px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  transition: all 0.15s ease;
}

.submit-modal-close:hover {
  background-color: #f1f5f9;
  color: #0f172a;
}

/* Stepper Bar */
.submit-stepper-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: 1px solid #e2e8f0;
  border-radius: 999px;
  padding: 7px 18px;
  background-color: #fbfcfd;
  margin-bottom: 20px;
  gap: 8px;
}

.submit-stepper-step {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  user-select: none;
  font-size: 12px;
  color: #94a3b8;
  font-weight: 500;
  transition: all 0.15s ease;
}

.submit-stepper-step.active {
  color: #0f172a;
  font-weight: 700;
}

.submit-stepper-step.completed {
  color: #059669;
  font-weight: 600;
}

.submit-stepper-circle {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
  background-color: #f1f5f9;
  color: #94a3b8;
  border: 1px solid #cbd5e1;
  transition: all 0.15s ease;
}

.submit-stepper-step.active .submit-stepper-circle {
  background-color: #0f172a;
  color: #ffffff;
  border-color: #0f172a;
}

.submit-stepper-step.completed .submit-stepper-circle {
  background-color: #10b981;
  color: #ffffff;
  border-color: #10b981;
}

.submit-stepper-line {
  flex: 1;
  height: 2px;
  background-color: #e2e8f0;
  border-radius: 2px;
  min-width: 14px;
  transition: background-color 0.2s ease;
}

.submit-stepper-line.completed {
  background-color: #10b981;
}

/* Step 1: Kind of Report Grid */
.submit-kind-scroll-wrap {
  overflow-y: auto;
  max-height: 380px;
  padding-right: 4px;
  margin-bottom: 8px;
}

.submit-kind-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
}

@media (max-width: 768px) {
  .submit-kind-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 8px;
  }
}

@media (max-width: 500px) {
  .submit-kind-grid {
    grid-template-columns: 1fr;
    gap: 8px;
  }
}

.submit-kind-card {
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 9px 10px 8px 10px;
  background-color: #ffffff;
  cursor: pointer;
  transition: all 0.15s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-height: 68px;
}

.submit-kind-card:hover {
  border-color: #94a3b8;
  background-color: #fafbfc;
  transform: translateY(-1px);
}

.submit-kind-card.selected {
  border: 2px solid #0f172a;
  box-shadow: 0 2px 10px rgba(15, 23, 42, 0.08);
  background-color: #ffffff;
}

.submit-kind-icon {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;
}

.submit-kind-icon.unselected {
  background-color: #f1f5f9;
  color: #334155;
}

.submit-kind-icon.selected {
  background-color: #0f172a;
  color: #ffffff;
}

.submit-kind-radio {
  width: 15px;
  height: 15px;
  border-radius: 50%;
  border: 1.5px solid #cbd5e1;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;
}

.submit-kind-radio.selected {
  background-color: #0f172a;
  border-color: #0f172a;
}

.submit-kind-radio-inner {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background-color: #ffffff;
}

.submit-kind-title {
  font-size: 11.5px;
  font-weight: 600;
  color: #0f172a;
  line-height: 1.25;
  margin-top: 2px;
}

/* Form Scroll & Fields */
.submit-form-scroll-wrap {
  overflow-y: auto;
  max-height: 420px;
  padding-right: 6px;
  padding-bottom: 8px;
}

.submit-form-fields-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
}

@media (max-width: 640px) {
  .submit-form-fields-grid {
    grid-template-columns: 1fr;
  }
}

.submit-field {
  display: flex;
  flex-direction: column;
}

.submit-field label {
  font-size: 12px;
  font-weight: 600;
  color: #334155;
  margin-bottom: 5px;
}

.submit-field input,
.submit-field textarea,
.submit-select {
  width: 100%;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  padding: 9px 12px;
  font-size: 13px;
  color: #0f172a;
  font-family: inherit;
  outline: none;
  background-color: #ffffff;
  transition: all 0.15s ease;
  box-sizing: border-box;
}

.submit-field input:focus,
.submit-field textarea:focus,
.submit-select:focus {
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.12);
}

.submit-severity-selector {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.submit-severity-pill {
  flex: 1;
  min-width: 70px;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 11.5px;
  font-weight: 600;
  border: 1px solid #cbd5e1;
  background-color: #f8fafc;
  color: #475569;
  cursor: pointer;
  transition: all 0.15s ease;
  text-align: center;
}

.submit-severity-pill.sev-low.active {
  background-color: #ecfdf5;
  color: #047857;
  border-color: #10b981;
}

.submit-severity-pill.sev-medium.active {
  background-color: #fffbeb;
  color: #b45309;
  border-color: #f59e0b;
}

.submit-severity-pill.sev-high.active {
  background-color: #fef2f2;
  color: #b91c1c;
  border-color: #ef4444;
}

.submit-severity-pill.sev-critical.active {
  background-color: #7f1d1d;
  color: #ffffff;
  border-color: #7f1d1d;
}

/* Step Footer & Action Buttons */
.submit-step-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 16px;
  padding-top: 14px;
  border-top: 1px solid #f1f5f9;
}

.submit-btn-back {
  background-color: #ffffff;
  border: 1px solid #cbd5e1;
  color: #334155;
  padding: 9px 22px;
  border-radius: 999px;
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.15s ease;
  font-family: inherit;
}

.submit-btn-back:hover:not(:disabled) {
  background-color: #f1f5f9;
  border-color: #94a3b8;
  color: #0f172a;
}

.submit-btn-next {
  background-color: #0f172a;
  border: none;
  color: #ffffff;
  padding: 10px 24px;
  border-radius: 999px;
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  transition: all 0.15s ease;
  font-family: inherit;
}

.submit-btn-next:hover:not(:disabled) {
  background-color: #1e293b;
  transform: translateY(-1px);
  box-shadow: 0 3px 8px rgba(15, 23, 42, 0.15);
}

.submit-btn-next:active {
  transform: translateY(0);
}

.submit-btn-next:disabled,
.submit-btn-back:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.submit-dropzone:hover {
  border-color: #2563eb !important;
  background-color: #eff6ff !important;
}

.submit-success-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 30px 20px;
  gap: 10px;
}

.submit-success-box h4 {
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
}

.submit-success-box p {
  font-size: 13px;
  color: #64748b;
}

/* ========================================================================= */
/* DASHBOARD PAGE (EXACT MOCKUP IMPLEMENTATION)                              */
/* ========================================================================= */
.dashboard-content-wrapper {
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding-bottom: 40px;
}

.dash-breadcrumb {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #64748b;
  font-weight: 500;
}

.dash-breadcrumb-item {
  color: #64748b;
}

.dash-breadcrumb-sep {
  color: #cbd5e1;
}

.dash-breadcrumb-current {
  color: #0f172a;
  font-weight: 600;
}

.dash-hero-section {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
}

.dash-greeting-wrap {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.dash-greeting-title {
  font-size: 26px;
  font-weight: 800;
  color: #0f172a;
  letter-spacing: -0.02em;
  display: flex;
  align-items: center;
  gap: 8px;
}

.dash-wave {
  display: inline-block;
  animation: wave 1.5s infinite;
  transform-origin: 70% 70%;
}

.dash-greeting-sub {
  font-size: 14px;
  color: #64748b;
  font-weight: 500;
}

/* Sticky Yellow Feedback Note */
.dash-note-pill {
  background-color: #fef9c3;
  border: 1px solid #fef08a;
  border-radius: 12px;
  padding: 8px 14px;
  display: flex;
  align-items: center;
  gap: 12px;
  max-width: 600px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}

.dash-note-content {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
}

.dash-note-tag {
  background-color: #0f172a;
  color: #ffffff;
  font-size: 10px;
  font-weight: 700;
  padding: 1px 6px;
  border-radius: 4px;
  flex-shrink: 0;
}

.dash-note-text {
  font-size: 11.5px;
  font-weight: 500;
  color: #854d0e;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.dash-note-date {
  font-size: 11px;
  color: #a16207;
  font-style: italic;
  white-space: nowrap;
  flex-shrink: 0;
}

.dash-note-add-btn {
  background-color: #fef08a;
  border: 1px solid #fde047;
  color: #854d0e;
  border-radius: 50%;
  width: 22px;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  transition: all 0.15s ease;
}

.dash-note-add-btn:hover {
  background-color: #fde047;
  color: #713f12;
}

.dash-note-modal-info {
  background-color: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 12px;
  color: #92400e;
  display: flex;
  align-items: center;
  gap: 8px;
  animation: fadeIn 0.15s ease;
}

/* 4 KPI Metrics Grid */
.dash-metrics-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
}

.dash-kpi-card {
  background-color: #ffffff;
  border: 1px solid #edf0f6;
  border-radius: 16px;
  padding: 20px 22px;
  box-shadow: var(--shadow-card);
  display: flex;
  flex-direction: column;
  transition: all 0.15s ease;
  cursor: pointer;
}

.dash-kpi-card:hover {
  transform: translateY(-2px);
  box-shadow: var(--shadow-md);
  border-color: #cbd5e1;
}

.dash-kpi-top {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
}

.dash-kpi-icon-wrap {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.icon-blue {
  background-color: #eff6ff;
  color: #2563eb;
}

.icon-amber {
  background-color: #fffbeb;
  color: #d97706;
}

.icon-purple {
  background-color: #f5f3ff;
  color: #7c3aed;
}

.icon-rose {
  background-color: #fef2f2;
  color: #dc2626;
}

.icon-gray {
  background-color: #f1f5f9;
  color: #475569;
}

.icon-blue-soft {
  background-color: #eff6ff;
  color: #3b82f6;
}

.icon-amber-soft {
  background-color: #fffbeb;
  color: #f59e0b;
}

.dash-kpi-title {
  font-size: 13px;
  font-weight: 600;
  color: #475569;
}

.dash-kpi-number {
  font-size: 32px;
  font-weight: 800;
  color: #0f172a;
  line-height: 1;
  margin-bottom: 14px;
}

.dash-kpi-indicators {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  font-size: 11.5px;
  font-weight: 600;
  color: #64748b;
}

.dash-ind-item {
  display: inline-flex;
  align-items: center;
  gap: 5px;
}

.dash-ind-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: transparent;
  border: 1px solid transparent;
  border-radius: 6px;
  padding: 3px 6px;
  margin: -3px -4px;
  font-family: inherit;
  font-size: 11.5px;
  font-weight: 600;
  color: #64748b;
  cursor: pointer;
  transition: all 0.15s ease-in-out;
  text-decoration: none;
  white-space: nowrap;
}

.dash-ind-btn:hover {
  background-color: #f1f5f9;
  border-color: #cbd5e1;
  color: #0f172a;
  transform: translateY(-1px);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
}

.dash-ind-btn:focus-visible {
  outline: 2px solid #2563eb;
  outline-offset: 1px;
}

.dash-ind-btn:active {
  transform: translateY(0);
}

.dash-ind-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  display: inline-block;
}

.dot-red {
  background-color: #ef4444;
}

.dot-blue {
  background-color: #2563eb;
}

.dot-amber {
  background-color: #f59e0b;
}

.dot-green {
  background-color: #10b981;
}

.dot-purple {
  background-color: #8b5cf6;
}

.dot-gray {
  background-color: #94a3b8;
}

/* Two Major Tables Grid */
.dash-tables-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
}

.dash-card {
  background-color: #ffffff;
  border: 1px solid #edf0f6;
  border-radius: 16px;
  padding: 20px 22px;
  box-shadow: var(--shadow-card);
  display: flex;
  flex-direction: column;
}

.dash-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}

.dash-card-header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.dash-card-icon-wrap {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.dash-card-title {
  font-size: 15px;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.2;
}

.dash-card-subtitle {
  font-size: 12px;
  color: #64748b;
  margin-top: 2px;
}

.dash-view-all-btn {
  background: transparent;
  border: none;
  cursor: pointer;
  color: #2563eb;
  font-size: 12.5px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  transition: all 0.15s ease;
  font-family: inherit;
}

.dash-view-all-btn:hover {
  color: #1d4ed8;
  gap: 7px;
}

/* Modern Dashboard Tables */
.dash-table-wrap {
  overflow-x: auto;
  overflow-y: auto;
  max-height: 360px;
  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 #f8fafc;
}

.dash-table-wrap::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}
.dash-table-wrap::-webkit-scrollbar-track {
  background: #f8fafc;
  border-radius: 4px;
}
.dash-table-wrap::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 4px;
}
.dash-table-wrap::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

.dash-table {
  width: 100%;
  border-collapse: collapse;
  text-align: left;
}

.dash-table th {
  padding: 10px 12px;
  font-size: 11px;
  font-weight: 700;
  color: #64748b;
  border-bottom: 1px solid #edf0f6;
  white-space: nowrap;
  position: sticky;
  top: 0;
  background-color: #ffffff;
  z-index: 2;
}

.dash-table td {
  padding: 12px;
  font-size: 13px;
  border-bottom: 1px solid #f8fafc;
  vertical-align: middle;
}

.dash-clickable-row {
  cursor: pointer;
  transition: background-color 0.12s ease;
}

.dash-clickable-row:hover {
  background-color: #f8fafc;
}

.dash-cell-code {
  font-family: monospace;
  font-weight: 700;
  color: #0f172a;
  font-size: 12.5px;
  white-space: nowrap;
}

.dash-cell-title {
  font-weight: 600;
  color: #1e293b;
  max-width: 200px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.dash-cell-kind {
  font-size: 12px;
  color: #475569;
  white-space: nowrap;
}

.dash-cell-received {
  font-size: 12px;
  color: #64748b;
  white-space: nowrap;
}

/* Status Pills */
.dash-status-pill {
  font-size: 11px;
  font-weight: 700;
  padding: 3px 8px;
  border-radius: 6px;
  display: inline-block;
  white-space: nowrap;
}

.dash-status-blue {
  background-color: #eff6ff;
  color: #1d4ed8;
}

.dash-status-amber {
  background-color: #fef3c7;
  color: #b45309;
}

.dash-status-cyan {
  background-color: #e0f2fe;
  color: #0284c7;
}

/* Severity Badges */
.dash-severity-pill {
  font-size: 10.5px;
  font-weight: 800;
  padding: 3px 8px;
  border-radius: 6px;
  display: inline-block;
  white-space: nowrap;
}

.dash-badge-critical {
  background-color: #dc2626;
  color: #ffffff;
}

.dash-badge-high {
  background-color: #fee2e2;
  color: #dc2626;
}

.dash-badge-medium {
  background-color: #fef3c7;
  color: #d97706;
}

.dash-badge-low {
  background-color: #dcfce7;
  color: #16a34a;
}

/* Due Date Layout */
.dash-duedate-wrap {
  display: flex;
  flex-direction: column;
}

.dash-duedate-date {
  font-size: 12px;
  font-weight: 600;
  color: #0f172a;
}

.dash-duedate-sub {
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
  margin-top: 1px;
}

.dash-duedate-sub.urgent {
  color: #dc2626;
}

.dash-duedate-sub.soon {
  color: #2563eb;
}

.dash-row-more-btn {
  background: transparent;
  border: none;
  cursor: pointer;
  color: #94a3b8;
  padding: 4px;
  border-radius: 6px;
  display: flex;
  align-items: center;
}

.dash-row-more-btn:hover {
  background-color: #f1f5f9;
  color: #0f172a;
}

.dash-row-menu {
  position: absolute;
  right: 20px;
  background-color: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  box-shadow: var(--shadow-md);
  padding: 4px;
  z-index: 20;
  display: flex;
  flex-direction: column;
}

.dash-row-menu button {
  background: transparent;
  border: none;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 500;
  color: #334155;
  cursor: pointer;
  text-align: left;
  border-radius: 4px;
  font-family: inherit;
}

.dash-row-menu button:hover {
  background-color: #f1f5f9;
  color: #0f172a;
}

/* Feed Lists & Timelines */
.dash-feed-timeline-list {
  display: flex;
  flex-direction: column;
  gap: 0;
  max-height: 360px;
  overflow-y: auto;
  padding-right: 4px;
  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 #f8fafc;
}

.dash-feed-timeline-list::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}
.dash-feed-timeline-list::-webkit-scrollbar-track {
  background: #f8fafc;
  border-radius: 4px;
}
.dash-feed-timeline-list::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 4px;
}
.dash-feed-timeline-list::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

.dash-feed-item-row {
  display: flex;
  gap: 14px;
}

.dash-feed-indicator {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 32px;
  flex-shrink: 0;
}

.dash-feed-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  color: #ffffff;
  font-size: 11.5px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.dash-feed-icon-node {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.dash-feed-timeline-line {
  width: 2px;
  background-color: #f1f5f9;
  flex: 1;
  margin: 4px 0;
  min-height: 22px;
}

.dash-feed-body {
  flex: 1;
  min-width: 0;
  padding-bottom: 18px;
}

.dash-feed-header-line {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
  flex-wrap: wrap;
}

.dash-feed-actor {
  font-size: 13px;
  font-weight: 700;
  color: #0f172a;
}

.dash-feed-dept {
  font-weight: 500;
  color: #64748b;
  font-size: 12px;
}

.dash-feed-timestamp {
  font-size: 11.5px;
  color: #94a3b8;
  white-space: nowrap;
}

.dash-feed-action-text {
  font-size: 12.5px;
  color: #334155;
  line-height: 1.45;
  margin: 3px 0 6px 0;
}

.dash-feed-tag-pill {
  display: inline-flex;
  align-items: center;
  font-size: 11px;
  font-weight: 600;
  color: #2563eb;
  background-color: #eff6ff;
  border: 1px solid #dbeafe;
  padding: 3px 9px;
  border-radius: 5px;
  cursor: pointer;
  transition: all 0.15s ease;
  font-family: inherit;
}

.dash-feed-tag-pill:hover {
  background-color: #dbeafe;
  color: #1d4ed8;
  text-decoration: underline;
  transform: translateY(-1px);
}

.dash-feed-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.dash-feed-item {
  display: flex;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 10px;
  cursor: pointer;
  transition: background-color 0.12s ease;
}

.dash-feed-item:hover {
  background-color: #f8fafc;
}

.dash-feed-avatar {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  color: #ffffff;
  font-size: 12px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.dash-feed-content {
  flex: 1;
  min-width: 0;
}

.dash-feed-title {
  font-size: 13px;
  font-weight: 600;
  color: #0f172a;
  line-height: 1.3;
}

.dash-feed-desc {
  font-size: 12px;
  color: #64748b;
  margin-top: 2px;
  line-height: 1.4;
}

.dash-feed-time {
  font-size: 11px;
  color: #94a3b8;
  margin-top: 4px;
}

/* Animations */
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes fadeInDown {
  from {
    opacity: 0;
    transform: translateY(-8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes scaleUp {
  from {
    opacity: 0;
    transform: scale(0.96);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

@keyframes wave {
  0% { transform: rotate(0deg); }
  20% { transform: rotate(14deg); }
  40% { transform: rotate(-8deg); }
  60% { transform: rotate(14deg); }
  80% { transform: rotate(-4deg); }
  100% { transform: rotate(0deg); }
}

/* Responsive adjustments */
@media (max-width: 1200px) {
  .dash-metrics-grid {
    grid-template-columns: repeat(2, 1fr);
  }
  .dash-tables-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .dash-metrics-grid {
    grid-template-columns: 1fr;
  }
  .top-header {
    padding: 0 16px;
  }
  
  .cal-modal-body {
    grid-template-columns: 1fr;
  }
}


/* ========================================================================= */
/* DASHBOARD CUSTOMIZATION & WIDGET EDIT BAR STYLES                         */
/* ========================================================================= */
.dash-widget-edit-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: #f1f5f9;
  border-bottom: 1px solid #e2e8f0;
  padding: 6px 12px;
  margin: -20px -22px 14px -22px;
  border-radius: 15px 15px 0 0;
  gap: 8px;
}

.dash-kpi-card .dash-widget-edit-bar {
  margin: -20px -22px 12px -22px;
}

.dash-edit-btn {
  background: #ffffff;
  border: 1px solid #cbd5e1;
  border-radius: 5px;
  padding: 4px 6px;
  cursor: pointer;
  color: #475569;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.12s ease;
}

.dash-edit-btn:hover:not(:disabled) {
  background-color: #e2e8f0;
  color: #0f172a;
}

.dash-edit-btn:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}

.dash-edit-select {
  padding: 3px 6px;
  font-size: 11px;
  font-weight: 600;
  border-radius: 5px;
  border: 1px solid #cbd5e1;
  background-color: #ffffff;
  color: #0f172a;
  cursor: pointer;
  font-family: inherit;
}

.dash-edit-btn-remove {
  background: transparent;
  border: none;
  color: #dc2626;
  cursor: pointer;
  padding: 4px;
  border-radius: 5px;
  display: flex;
  align-items: center;
  transition: all 0.12s ease;
}

.dash-edit-btn-remove:hover {
  background-color: #fee2e2;
}

.dash-kpi-card.customizing {
  border: 2px dashed #93c5fd;
}


/* ========================================================================= */
/* HEADER LOGOUT & PROFILE DETAILS BOX STYLES                                */
/* ========================================================================= */
.header-logout-btn {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: var(--radius-sm);
  width: 38px;
  height: 38px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #64748b;
  cursor: pointer;
  transition: all 0.15s ease;
}

.header-logout-btn:hover {
  background-color: #fee2e2;
  color: #dc2626;
  border-color: #fca5a5;
}

.profile-dropdown-user-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 4px 4px 8px 4px;
}

.profile-dropdown-user-meta {
  min-width: 0;
  flex: 1;
}

.profile-dropdown-user-name {
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.profile-dropdown-user-email {
  font-size: 12px;
  color: #64748b;
  margin: 2px 0 0 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.profile-dropdown-details-box {
  background-color: #f8fafc;
  border: 1px solid #e9eef5;
  border-radius: 10px;
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 7px;
  margin: 8px 0;
}

.profile-detail-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.profile-detail-icon {
  color: #2563eb;
  margin-top: 2px;
  flex-shrink: 0;
}

.profile-detail-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.profile-detail-label {
  font-size: 10px;
  font-weight: 700;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  line-height: 1;
}

.profile-detail-value {
  font-size: 11.5px;
  font-weight: 600;
  color: #1e293b;
  margin-top: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.account-info-field label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  color: #475569;
  margin-bottom: 6px;
}

.account-info-field input {
  width: 100%;
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid #cbd5e1;
  font-size: 13px;
  color: #0f172a;
  background-color: #ffffff;
  outline: none;
  font-family: inherit;
  transition: all 0.15s ease;
}

.account-info-field input:focus {
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
}


/* ========================================================================= */
/* USER PROFILE & AUTHORIZED MODULES / SYSTEM SETTINGS PAGES                 */
/* ========================================================================= */
.profile-page-container {
  padding: 0 0 40px 0;
  max-width: 1200px;
}

.profile-page-breadcrumbs {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #64748b;
  margin-bottom: 12px;
}

.profile-crumb-link {
  color: #64748b;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  font-size: inherit;
  font-weight: 500;
  font-family: inherit;
  transition: color 0.15s ease;
}

.profile-crumb-link:hover {
  color: #0f172a;
  text-decoration: underline;
}

.profile-crumb-separator {
  color: #94a3b8;
}

.profile-crumb-text {
  color: #64748b;
  font-weight: 500;
}

.profile-crumb-current {
  color: #0f172a;
  font-weight: 600;
}

.profile-page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 16px;
}

.profile-page-title {
  font-size: 24px;
  font-weight: 800;
  color: #0f172a;
  margin: 0;
  letter-spacing: -0.02em;
}

.profile-nav-tabs {
  display: inline-flex;
  align-items: center;
  background-color: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 9999px;
  padding: 4px;
  gap: 4px;
  box-shadow: var(--shadow-sm);
}

.profile-nav-tab {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 16px;
  border-radius: 9999px;
  border: none;
  background: transparent;
  font-size: 13px;
  font-weight: 600;
  color: #475569;
  cursor: pointer;
  transition: all 0.15s ease;
  font-family: inherit;
}

.profile-nav-tab:hover {
  color: #0f172a;
  background-color: #f1f5f9;
}

.profile-nav-tab.active {
  background-color: #1e293b;
  color: #ffffff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
}

.profile-settings-card {
  background-color: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 28px 32px;
  box-shadow: var(--shadow-sm);
}

.profile-settings-search-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 26px;
  flex-wrap: wrap;
}

.profile-search-input-wrap {
  position: relative;
  flex: 1;
  max-width: 440px;
}

.profile-search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
  pointer-events: none;
}

.profile-search-input {
  width: 100%;
  height: 38px;
  padding: 0 36px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  font-size: 13px;
  background-color: #f8fafc;
  font-family: inherit;
  color: #0f172a;
  transition: all 0.15s ease;
}

.profile-search-input:focus {
  background-color: #ffffff;
  outline: none;
  border-color: #2563eb;
  box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.15);
}

.profile-search-clear-btn {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
  cursor: pointer;
  padding: 2px 6px;
}

.profile-authorized-count-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background-color: #eff6ff;
  border: 1px solid #bfdbfe;
  color: #1e40af;
  padding: 6px 14px;
  border-radius: 9999px;
  font-size: 12px;
  font-weight: 500;
}

.profile-permissions-sections {
  display: flex;
  flex-direction: column;
}

.profile-permission-category-block {
  margin-bottom: 20px;
}

.profile-permission-category-title {
  font-size: 14.5px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 12px 0;
  letter-spacing: -0.01em;
}

.profile-permission-pills-wrap {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}

.profile-permission-pill {
  background-color: #64748b;
  color: #ffffff;
  font-size: 11.5px;
  font-weight: 600;
  padding: 4.5px 11px;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  transition: all 0.15s ease;
  user-select: none;
  letter-spacing: 0.01em;
}

.profile-permission-pill.clickable {
  cursor: pointer;
}

.profile-permission-pill.clickable:hover {
  background-color: #334155;
  transform: translateY(-1px);
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.12);
}

.profile-pill-arrow {
  opacity: 0.7;
}

.profile-category-divider {
  border-bottom: 1px solid #f1f5f9;
  margin-top: 20px;
}

.profile-no-results {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 40px;
  color: #94a3b8;
  font-size: 14px;
}

.profile-card-user-banner {
  display: flex;
  align-items: center;
  gap: 18px;
  padding: 20px 24px;
  background-color: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
}

.profile-banner-avatar {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  font-weight: 700;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
  flex-shrink: 0;
}

.profile-banner-meta {
  flex: 1;
  min-width: 0;
}

.profile-banner-name {
  font-size: 20px;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
}

.profile-active-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background-color: #dcfce7;
  color: #15803d;
  font-size: 11.5px;
  font-weight: 700;
  padding: 2px 9px;
  border-radius: 9999px;
}

.profile-banner-subtitle {
  font-size: 13.5px;
  font-weight: 600;
  color: #2563eb;
  margin-top: 2px;
}

.profile-banner-org {
  font-size: 12.5px;
  color: #64748b;
  margin-top: 3px;
}

.profile-form-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 18px;
}

.profile-form-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.profile-form-group label {
  font-size: 12.5px;
  font-weight: 600;
  color: #334155;
  display: flex;
  align-items: center;
  gap: 6px;
}

.profile-form-group input {
  height: 38px;
  padding: 0 12px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  font-size: 13px;
  background-color: #ffffff;
  font-family: inherit;
  color: #0f172a;
  transition: all 0.15s ease;
}

.profile-form-group input:focus {
  outline: none;
  border-color: #2563eb;
  box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.15);
}

.profile-aviation-scope-box {
  background-color: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 14px 16px;
}

.profile-scope-title {
  font-size: 12px;
  font-weight: 700;
  color: #334155;
  margin-bottom: 4px;
}

.profile-scope-content {
  font-size: 12.5px;
  color: #475569;
  line-height: 1.5;
}

.profile-alert {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
}

.profile-alert.success {
  background-color: #ecfdf5;
  border: 1px solid #a7f3d0;
  color: #065f46;
}

.profile-alert.error {
  background-color: #fef2f2;
  border: 1px solid #fecaca;
  color: #991b1b;
}

.change-password-header {
  margin-bottom: 16px;
}

.change-password-title {
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
  margin: 0 0 4px 0;
}

.change-password-subtitle {
  font-size: 13px;
  color: #64748b;
  margin: 0;
}

.profile-card-divider {
  border-bottom: 1px solid #f1f5f9;
  margin-bottom: 20px;
}

.change-password-card {
  max-width: 580px;
  padding: 32px 36px;
}

.change-password-form {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.password-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
}

.password-input-wrapper input {
  width: 100%;
  height: 42px;
  padding: 0 42px 0 14px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  font-size: 13.5px;
  color: #0f172a;
  background-color: #ffffff;
  outline: none;
  font-family: inherit;
  transition: all 0.15s ease;
}

.password-input-wrapper input:focus {
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.12);
}

.password-toggle-btn {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: transparent;
  border: none;
  cursor: pointer;
  color: #94a3b8;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  transition: color 0.15s ease;
}

.password-toggle-btn:hover {
  color: #334155;
}

.password-hint-text {
  font-size: 11.5px;
  color: #64748b;
  margin-top: 4px;
}

.change-password-action-row {
  display: flex;
  justify-content: flex-end;
  margin-top: 10px;
}

.btn-update-password {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background-color: #1e293b;
  color: #ffffff;
  padding: 11px 22px;
  border-radius: 8px;
  border: none;
  font-size: 13.5px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
  font-family: inherit;
}

.btn-update-password:hover:not(:disabled) {
  background-color: #0f172a;
  transform: translateY(-1px);
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.15);
}

.btn-update-password:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.spin-animate {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* ========================================================================= */
/* KPI CARD INDICATOR BREAKDOWN POP-UP MODAL                                 */
/* ========================================================================= */
.dash-ind-modal-table-wrap {
  width: 100%;
  overflow-x: auto;
}

.dash-ind-modal-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 12.5px;
  text-align: left;
}

.dash-ind-modal-table th {
  background-color: #f8fafc;
  color: #475569;
  font-weight: 600;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 11px 14px;
  border-bottom: 1px solid #e2e8f0;
  white-space: nowrap;
}

.dash-ind-modal-table td {
  padding: 12px 14px;
  border-bottom: 1px solid #f1f5f9;
  vertical-align: middle;
  color: #1e293b;
}

.dash-ind-modal-table tr:hover td {
  background-color: #f8fafc;
}

.dash-ind-ref-badge {
  display: inline-flex;
  align-items: center;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 12px;
  font-weight: 700;
  color: #1d4ed8;
  background-color: #eff6ff;
  border: 1px solid #bfdbfe;
  padding: 2.5px 7px;
  border-radius: 5px;
  letter-spacing: 0.02em;
}

.dash-ind-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.02em;
  white-space: nowrap;
}

.dash-ind-badge-red {
  background-color: #fef2f2;
  color: #b91c1c;
  border: 1px solid #fecaca;
}

.dash-ind-badge-amber {
  background-color: #fffbeb;
  color: #b45309;
  border: 1px solid #fde68a;
}

.dash-ind-badge-blue {
  background-color: #eff6ff;
  color: #1d4ed8;
  border: 1px solid #bfdbfe;
}

.dash-ind-badge-green {
  background-color: #ecfdf5;
  color: #047857;
  border: 1px solid #a7f3d0;
}

.dash-ind-badge-purple {
  background-color: #f5f3ff;
  color: #6d28d9;
  border: 1px solid #ddd6fe;
}

.dash-ind-badge-gray {
  background-color: #f8fafc;
  color: #475569;
  border: 1px solid #cbd5e1;
}

.dash-ind-action-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background-color: #ffffff;
  border: 1px solid #cbd5e1;
  color: #1e293b;
  padding: 6px 11px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;
  font-family: inherit;
}

.dash-ind-action-btn:hover {
  background-color: #2563eb;
  border-color: #2563eb;
  color: #ffffff;
  box-shadow: 0 2px 5px rgba(37, 99, 235, 0.25);
  transform: translateY(-1px);
}

.dash-ind-action-btn:active {
  transform: translateY(0);
}


/* ==========================================================================
   Module Analysis: Custom Analysis & Customizable Dashboards
   ========================================================================== */

.analysis-page-container {
  padding: 24px 28px;
  background-color: #f8fafc;
  min-height: 100%;
  overflow-y: auto;
}

.analysis-grid {
  display: grid;
  grid-template-columns: repeat(12, minmax(0, 1fr));
  gap: 20px;
  width: 100%;
}

.analysis-col-3 {
  grid-column: span 3 / span 3;
}

.analysis-col-4 {
  grid-column: span 4 / span 4;
}

.analysis-col-6 {
  grid-column: span 6 / span 6;
}

.analysis-col-8 {
  grid-column: span 8 / span 8;
}

.analysis-col-12 {
  grid-column: 1 / -1;
}

@media (max-width: 1024px) {
  .analysis-col-3,
  .analysis-col-4,
  .analysis-col-6,
  .analysis-col-8 {
    grid-column: span 6 / span 6;
  }
}

@media (max-width: 768px) {
  .analysis-grid {
    grid-template-columns: 1fr;
  }
  .analysis-col-3,
  .analysis-col-4,
  .analysis-col-6,
  .analysis-col-8,
  .analysis-col-12 {
    grid-column: 1 / -1;
  }
}

.analysis-widget-card {
  background-color: #ffffff;
  border-radius: 14px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
  transition: all 0.2s ease;
}

.analysis-widget-card:hover {
  box-shadow: 0 6px 18px rgba(15, 23, 42, 0.08);
}

.analysis-widget-header {
  min-height: 54px;
  padding: 12px 18px;
  border-bottom: 1px solid #f1f5f9;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: #ffffff;
}

.analysis-widget-body {
  width: 100%;
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
}

.analysis-chart-wrapper {
  width: 100%;
  position: relative;
  min-height: 200px;
}
